import type { RecipeShoppingList } from '../domain/model';
import type { HandoffTarget } from '../application/contracts';
import { recipeShoppingItemLabel } from '../i18n/domain-labels';
import { useI18n } from '../i18n/I18nContext';
import { Button, Field, Icon, Modal, unitLabel } from './components';

export type ShoppingListHandoffView =
  | { readonly stage: 'checking' | 'disconnected' | 'available' }
  | { readonly stage: 'identity_pending'; readonly expiresAt: string }
  | { readonly stage: 'trial_eligible' | 'trial_expired' | 'declined' | 'unavailable'; readonly fallbackUrl: string | null }
  | {
      readonly stage: 'issued' | 'awaiting_target' | 'wrapped';
      readonly targetUrl: string;
      readonly intentId: string;
      readonly expiresAt: string;
    }
  | {
      readonly stage: 'awaiting_receipt';
      readonly intentId: string;
      readonly expiresAt: string;
    }
  | { readonly stage: 'accepted'; readonly intentId: string }
  | { readonly stage: 'receipt_declined' | 'receipt_expired' | 'receipt_invalid' | 'receipt_unavailable' };

export const ShoppingListHandoffModal = ({
  list,
  target,
  busy,
  view,
  email,
  onClose,
  onConnect,
  onDisconnect,
  onEmailChange,
  onPrepare,
  onRetryKeyExchange,
  onRetryReceipt,
  onTargetOpened,
}: {
  list: RecipeShoppingList;
  target: HandoffTarget;
  busy: boolean;
  view: ShoppingListHandoffView;
  email: string;
  onClose: () => void;
  onConnect: () => Promise<void>;
  onDisconnect: () => Promise<void>;
  onEmailChange: (value: string) => void;
  onPrepare: () => Promise<void>;
  onRetryKeyExchange: () => Promise<void>;
  onRetryReceipt: () => Promise<void>;
  onTargetOpened: () => Promise<void>;
}) => {
  const { formatNumber, locale, t } = useI18n();
  const targetName = target === 'paniprix' ? 'PaniPrix' : 'ARCHIBALD';
  const targetParam = { target: targetName };
  const formatHandoffDateTime = (value: string) => new Intl.DateTimeFormat(
    locale === 'en-XA' ? 'en' : locale,
    { dateStyle: 'short', timeStyle: 'short' },
  ).format(new Date(value));
  const connected = !['checking', 'disconnected', 'identity_pending'].includes(view.stage);
  const activeTransfer = view.stage === 'issued' || view.stage === 'awaiting_target' ||
    view.stage === 'wrapped' || view.stage === 'awaiting_receipt';

  return (
    <Modal title={t('handoff.title', targetParam)} description={t('handoff.description')} onClose={onClose}>
      <div className="handoff-preview">
        <div className="notice notice--info">
          <Icon name="info" />
          <p>{t('handoff.deduplication', targetParam)}</p>
        </div>
        <ul className="shopping-list-items">
          {list.items.map((item) => (
            <li key={item.id}>
              <span aria-hidden="true">{item.checked ? '✓' : '○'}</span>
              <span>{recipeShoppingItemLabel(item, locale)}</span>
              <small>
                {item.requiredQuantity == null || item.unit == null
                  ? t('shopping.quantityToCheck')
                  : `${formatNumber(item.requiredQuantity)} ${unitLabel(item.unit, locale, item.requiredQuantity)}`}
              </small>
            </li>
          ))}
        </ul>

        {view.stage === 'checking' ? (
          <p className="persistence-status" role="status">{t('handoff.checkingAccess', targetParam)}</p>
        ) : null}

        {view.stage === 'disconnected' ? (
          <div className="handoff-session-panel">
            <Field
              label={t('handoff.identity')}
              hint={t('handoff.identityHint')}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => onEmailChange(event.target.value)}
            />
            <Button disabled={busy} onClick={() => void onConnect()} icon={<Icon name="arrow" />}>
              {t('handoff.connect')}
            </Button>
          </div>
        ) : null}

        {view.stage === 'identity_pending' ? (
          <div className="notice notice--info" role="status">
            <Icon name="info" />
            <div>
              <p>{t('handoff.identityPending')}</p>
              <small>{t('handoff.identityExpiresAt', { date: formatHandoffDateTime(view.expiresAt) })}</small>
            </div>
          </div>
        ) : null}

        {view.stage === 'available' ? (
          <div className="notice notice--success" role="status">
            <Icon name="check" />
            <p>{t('handoff.accessReady', targetParam)}</p>
          </div>
        ) : null}

        {view.stage === 'trial_eligible' ? (
          <div className="notice notice--warning" role="status">
            <Icon name="warning" />
            <div>
              <p>{t('handoff.trialEligible', targetParam)}</p>
              {view.fallbackUrl != null ? (
                <a className="button button--secondary" href={view.fallbackUrl} target="_blank" rel="noreferrer noopener">
                  {t('handoff.activateTrial', targetParam)}
                </a>
              ) : null}
              <Button variant="ghost" disabled={busy} onClick={() => void onConnect()}>
                {t('handoff.recheckAccess')}
              </Button>
            </div>
          </div>
        ) : null}

        {view.stage === 'trial_expired' || view.stage === 'declined' || view.stage === 'unavailable' ? (
          <div className="notice notice--warning" role="status">
            <Icon name="warning" />
            <div>
              <p>{t(view.stage === 'trial_expired'
                ? 'handoff.trialExpired'
                : view.stage === 'declined'
                  ? 'handoff.declined'
                  : 'handoff.unavailable')}</p>
              {view.fallbackUrl != null ? (
                <a className="button button--secondary" href={view.fallbackUrl} target="_blank" rel="noreferrer noopener">
                  {t('handoff.openFallback', targetParam)}
                </a>
              ) : null}
            </div>
          </div>
        ) : null}

        {activeTransfer ? (
          <div className="handoff-transfer-panel" data-testid="handoff-public-transfer">
            <div className="notice notice--info" role="status">
              <Icon name="info" />
              <div>
                <p>{t(view.stage === 'wrapped' || view.stage === 'awaiting_receipt'
                  ? 'handoff.keyWrapped'
                  : view.stage === 'awaiting_target'
                    ? 'handoff.awaitingTarget'
                    : 'handoff.intentReady', targetParam)}</p>
                <small>{t('handoff.expiresAt', { date: formatHandoffDateTime(view.expiresAt) })}</small>
              </div>
            </div>
            {view.stage !== 'awaiting_receipt' ? (
              <a
                className="button button--primary"
                href={view.targetUrl}
                target="_blank"
                rel="noreferrer noopener"
                onClick={() => void onTargetOpened()}
              >
                {t(view.stage === 'wrapped' ? 'handoff.returnToTarget' : 'handoff.openTarget', targetParam)}
              </a>
            ) : null}
            {view.stage === 'awaiting_target' ? (
              <Button variant="secondary" disabled={busy} onClick={() => void onRetryKeyExchange()}>
                {t('handoff.retryKeyExchange', targetParam)}
              </Button>
            ) : null}
            {view.stage === 'wrapped' || view.stage === 'awaiting_receipt' ? (
              <div className="handoff-receipt-panel">
                <p role="status">{t('handoff.awaitingReceipt', targetParam)}</p>
                <Button disabled={busy} onClick={() => void onRetryReceipt()} icon={<Icon name="check" />}>
                  {t('handoff.retryReceipt', targetParam)}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}

        {view.stage === 'accepted' ? (
          <div className="notice notice--success" role="status" data-testid="handoff-source-release">
            <Icon name="check" />
            <p>{t('handoff.acceptedVerified', targetParam)}</p>
          </div>
        ) : null}

        {view.stage === 'receipt_declined' || view.stage === 'receipt_expired' ||
        view.stage === 'receipt_invalid' || view.stage === 'receipt_unavailable' ? (
          <div className="notice notice--warning" role="status">
            <Icon name="warning" />
            <p>{t(view.stage === 'receipt_declined'
              ? 'handoff.receiptDeclined'
              : view.stage === 'receipt_expired'
                ? 'handoff.receiptExpired'
                : view.stage === 'receipt_unavailable'
                  ? 'handoff.receiptUnavailable'
                  : 'handoff.receiptInvalid')}</p>
            {view.stage === 'receipt_unavailable' ? (
              <Button disabled={busy} variant="secondary" onClick={() => void onRetryReceipt()}>
                {t('handoff.retryReceipt', targetParam)}
              </Button>
            ) : null}
          </div>
        ) : null}

        <p className="microcopy">{t('handoff.sourceAlwaysRetained')}</p>
        <div className="button-stack">
          {view.stage === 'available' ? (
            <Button onClick={() => void onPrepare()} disabled={busy || list.items.length === 0} icon={<Icon name="arrow" />}>
              {t('handoff.confirm', targetParam)}
            </Button>
          ) : null}
          {connected ? (
            <Button variant="ghost" disabled={busy} onClick={() => void onDisconnect()}>{t('handoff.disconnect')}</Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>{t('handoff.cancel')}</Button>
        </div>
      </div>
    </Modal>
  );
};
